import { useEffect, useState } from "react";
import {
  Form,
  Link,
  redirect,
  useActionData,
  useNavigation,
  useSearchParams,
  type ActionFunctionArgs,
} from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { formError, loadSession, safeNext, type FormError } from "@/lib/session";
import {
  AuthCard,
  ErrorNote,
  Field,
  Honeypot,
  ProviderButtons,
  RESEND_AFTER_SECONDS,
  ResendForm,
  SubmitButton,
} from "@/components/form";
import { Button } from "@/components/ui/button";

/**
 * Signing up is the address alone (0030). The api sends it a link, and the
 * page the link opens chooses the password — only that creates the account,
 * so nobody can make one with an address they do not hold. The page says
 * "check your email" whether or not the address has an account already:
 * that one is sent "you already have an account" instead, and only its
 * owner learns which.
 */

export async function signUpLoader({ request }: { request: Request }) {
  const [session] = await Promise.all([loadSession(), read(queries.providers())]);
  if (session) {
    throw redirect(safeNext(new URL(request.url).searchParams.get("next")));
  }
  return null;
}

type SignUpResult = Partial<FormError> & {
  /** The address it was sent to, once asked. */
  email?: string;
  /** Whether the email went out: false when the provider refused it. */
  sent?: boolean;
  /** When it was asked, so each answer starts the resend wait afresh. */
  askedAt?: number;
};

export async function signUpAction({ request }: ActionFunctionArgs): Promise<SignUpResult | Response> {
  const form = await request.formData();
  const email = String(form.get("email") ?? "").trim();
  const next = safeNext(form.get("next"));

  let response;
  try {
    response = await api.auth.signUp({
      email,
      // Home is where a link lands anyway; only a real destination
      // is worth carrying through the email.
      next: next === "/" ? undefined : next,
      website: String(form.get("website") ?? "") || undefined,
    });
  } catch (error) {
    return { ...formError(error, "Could not send the link"), email };
  }

  // Where no mail is sent, the api hands the link back, and its page opens
  // at once. The token stays in the fragment, as it would in an email.
  if (response.token) {
    return redirect(`/sign-up/finish#token=${encodeURIComponent(response.token)}`);
  }
  return { email, sent: response.verificationEmailSent, askedAt: Date.now() };
}

export default function SignUp() {
  const { data: providers } = useSuspenseQuery(queries.providers());
  const result = useActionData() as SignUpResult | undefined;
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  // "Use a different address" goes back to the form without leaving the
  // page, until the next answer comes.
  const [askingAgain, setAskingAgain] = useState(false);
  useEffect(() => setAskingAgain(false), [result]);

  const next = safeNext(searchParams.get("next"));
  const submitting = navigation.state === "submitting";
  const signInHref = next === "/" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(next)}`;

  if (result?.sent !== undefined && result.email && !askingAgain) {
    return (
      <CheckYourEmail
        // A new answer starts the wait afresh.
        key={result.askedAt}
        email={result.email}
        sent={result.sent}
        next={next}
        signInHref={signInHref}
        onDifferentAddress={() => setAskingAgain(true)}
      />
    );
  }

  return (
    <AuthCard
      title="Create an account"
      footer={
        <>
          Already have one?{" "}
          <Link to={signInHref} className="font-medium underline underline-offset-4">
            Sign in
          </Link>
        </>
      }
    >
      {result?.error && !result.field && <ErrorNote message={result.error} />}

      <Form method="post" replace className="relative space-y-4">
        <input type="hidden" name="next" value={next} />
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          defaultValue={result?.email}
          error={result?.field === "email" ? result.error : undefined}
          hint="We'll email you a link to choose your password."
        />
        <Honeypot />
        <SubmitButton pending={submitting}>{submitting ? "Sending…" : "Continue"}</SubmitButton>
      </Form>

      <ProviderButtons providers={providers} next={next} />
    </AuthCard>
  );
}

/**
 * After asking: where the link went, and what to do if it does not arrive.
 * Sending it again waits a minute, as the api does — sooner would send
 * nothing — except when the provider refused the first, which is retried at
 * once.
 */
function CheckYourEmail({
  email,
  sent,
  next,
  signInHref,
  onDifferentAddress,
}: {
  email: string;
  sent: boolean;
  next: string;
  signInHref: string;
  onDifferentAddress: () => void;
}) {
  return (
    <AuthCard
      title={sent ? "Check your email" : "The email didn't go out"}
      footer={
        <Link to={signInHref} className="font-medium underline underline-offset-4">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <p className="text-sm text-muted-foreground">
          We sent a link to <strong>{email}</strong>. Open it to choose your password — it
          works once and expires in 24 hours. If you already have an account, the email says
          so instead, with a way back in.
        </p>
      ) : (
        <p role="alert" className="text-sm text-muted-foreground">
          We couldn't send the email to <strong>{email}</strong> just now. Nothing has been
          created yet — try again in a moment.
        </p>
      )}

      {/* A refused email is retried at once: nothing was counted. */}
      <ResendForm fields={{ email, next }} wait={sent ? RESEND_AFTER_SECONDS : 0} />
      {sent && (
        <p className="text-xs text-muted-foreground">Not there? Check your spam or junk folder.</p>
      )}
      <Button type="button" variant="ghost" size="lg" className="w-full" onClick={onDifferentAddress}>
        Use a different address
      </Button>
    </AuthCard>
  );
}
